import { useCallback } from 'react';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { useTranslation } from 'react-i18next';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context';
import { SubscriptionManagerService } from 'src/pages/Settings/components/SubscriptionsManager/SubscriptionManager.service';
import { extractSetupIntentPaymentMethodId } from 'src/pages/Settings/components/SubscriptionsManager/SubscriptionManager.functions';
import { LLog } from 'src/lib/logging';

export type tSeatCardAttachResult =
  | { ok: true }
  | { ok: false; message: string };

const LOG_CONTEXT = {
  component: 'useSeatCardAttach',
  method: 'attachCard',
  operation: 'attach-card',
};

/**
 * Attach the card the customer just typed into the inline capture block, before the
 * seat is charged.
 *
 * Without this the capture block is decorative: it collects card details and the
 * purchase then runs against a customer who still has no payment method, so the charge
 * fails every time. Collecting a card and not attaching it is worse than not offering
 * the field at all.
 *
 * The sequence mirrors the Subscription tab's existing add-card flow so both go
 * through the same Stripe contract: submit the element, ask the server for a
 * SetupIntent, confirm it, then promote the resulting card to default.
 *
 * That last step is load-bearing for the decline path. Attaching a card does NOT make
 * it the provider default, and the card that just failed stays default — so without
 * the promotion, "use a different card" would collect a new card and then retry the
 * charge against the old, failing one.
 */
export function useSeatCardAttach(): {
  attachCard: () => Promise<tSeatCardAttachResult>;
} {
  const { t } = useTranslation('settings/SubscriptionManager');
  const stripe = useStripe();
  const elements = useElements();
  const { currentWorkspace } = useWorkspaceContext();
  const workspaceId = currentWorkspace?.id ?? '';

  const attachCard = useCallback(async (): Promise<tSeatCardAttachResult> => {
    if (!stripe || !elements || !workspaceId) {
      return { ok: false, message: t('errors.generic') };
    }

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        // Incomplete or invalid card details — Stripe already explains which.
        return {
          ok: false,
          message: submitError.message || t('errors.generic'),
        };
      }

      const clientSecret =
        await SubscriptionManagerService.attachPaymentMethodToCustomer(
          workspaceId,
        );

      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        clientSecret: clientSecret.data,
        confirmParams: {
          return_url: import.meta.env.VITE_BASE_URL_FRONTEND + '/settings',
        },
        redirect: 'if_required' as const,
      });

      if (error) {
        return { ok: false, message: error.message || t('errors.generic') };
      }

      const addedPaymentMethodId =
        extractSetupIntentPaymentMethodId(setupIntent);

      if (addedPaymentMethodId) {
        try {
          await SubscriptionManagerService.makePaymentMethodDefault(
            addedPaymentMethodId,
            workspaceId,
          );
        } catch (defaultError) {
          // Does not block the purchase: the card is attached, so the charge below
          // can still go through. What it costs is the guarantee that the RETRY uses
          // the new card rather than the one that just declined — worth logging, not
          // worth refusing the seat over.
          LLog.error(
            LOG_CONTEXT,
            'Card attached but could not be promoted to default',
            defaultError instanceof Error ? defaultError : undefined,
            { workspaceId },
          );
        }
      }

      return { ok: true };
    } catch (error) {
      LLog.error(
        LOG_CONTEXT,
        'Could not attach a card for the seat purchase',
        error instanceof Error ? error : undefined,
        { workspaceId },
      );
      return { ok: false, message: t('errors.generic') };
    }
  }, [stripe, elements, workspaceId, t]);

  return { attachCard };
}
